import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, RefreshCw, Trash2, ExternalLink } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CitationsBox({ citations, onRefresh, citationStyle }) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    source_url: '',
    authors: '',
    publication_date: '',
    source_type: 'website',
    doi: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);

  const handleAddCitation = async (e) => {
    e.preventDefault();
    
    if (!formData.title) {
      toast.error('Title is required');
      return;
    }

    setLoading(true);
    try {
      const authorsArray = formData.authors
        ? formData.authors.split(',').map(a => a.trim()).filter(Boolean)
        : [];

      await axios.post(
        `${API}/admin/generation/citations`,
        {
          title: formData.title,
          source_url: formData.source_url || null,
          authors: authorsArray,
          publication_date: formData.publication_date || null,
          source_type: formData.source_type,
          doi: formData.doi || null,
          notes: formData.notes || null
        },
        { withCredentials: true }
      );

      toast.success('Citation added!');
      setFormData({
        title: '',
        source_url: '',
        authors: '',
        publication_date: '',
        source_type: 'website',
        doi: '',
        notes: ''
      });
      setShowAddForm(false);
      onRefresh();
    } catch (error) {
      console.error('Error adding citation:', error);
      toast.error('Failed to add citation');
    } finally {
      setLoading(false);
    }
  };

  const handleFormatCitation = async (citationId) => {
    try {
      const response = await axios.post(
        `${API}/admin/generation/citations/format`,
        {
          citation_id: citationId,
          style: citationStyle
        },
        { withCredentials: true }
      );

      return response.data.formatted_citation;
    } catch (error) {
      console.error('Error formatting citation:', error);
      return null;
    }
  };

  const handleGenerateReferences = async () => {
    if (citations.length === 0) {
      toast.error('No citations to format');
      return;
    }

    try {
      let referencesText = `References (${citationStyle} Style)\n\n`;
      
      for (const citation of citations) {
        const formatted = await handleFormatCitation(citation.id);
        if (formatted) {
          referencesText += formatted + '\n\n';
        }
      }

      // Copy to clipboard
      navigator.clipboard.writeText(referencesText);
      toast.success('References copied to clipboard!');
    } catch (error) {
      console.error('Error generating references:', error);
      toast.error('Failed to generate references');
    }
  };

  return (
    <div className="mt-8 admin-card border-4 border-[#6B4423]">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-3xl font-bold text-white">
          Citations (Box 21)
        </h2>
        <div className="flex gap-2">
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="retro-button flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Citation
          </button>
          <button
            onClick={onRefresh}
            className="retro-button flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
          {citations.length > 0 && (
            <button
              onClick={handleGenerateReferences}
              className="retro-button bg-green-600 hover:bg-green-700"
            >
              Generate Reference Page
            </button>
          )}
        </div>
      </div>

      {/* Add Citation Form */}
      {showAddForm && (
        <div className="mb-6 p-4 bg-[#F4E4C1] rounded border-2 border-[#8B6F47]">
          <h3 className="text-xl font-bold text-[#6B4423] mb-4">Add New Citation</h3>
          <form onSubmit={handleAddCitation} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="block text-sm font-semibold text-[#6B4423] mb-1">
                  Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-3 py-2 border-2 border-[#8B6F47] rounded"
                  required
                />
              </div>
              
              <div className="col-span-2">
                <label className="block text-sm font-semibold text-[#6B4423] mb-1">
                  URL
                </label>
                <input
                  type="url"
                  value={formData.source_url}
                  onChange={(e) => setFormData(prev => ({ ...prev, source_url: e.target.value }))}
                  placeholder="https://..."
                  className="w-full px-3 py-2 border-2 border-[#8B6F47] rounded"
                />
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-[#6B4423] mb-1">
                  Authors (comma-separated)
                </label>
                <input
                  type="text"
                  value={formData.authors}
                  onChange={(e) => setFormData(prev => ({ ...prev, authors: e.target.value }))}
                  placeholder="Smith, J., Doe, A."
                  className="w-full px-3 py-2 border-2 border-[#8B6F47] rounded"
                />
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-[#6B4423] mb-1">
                  Publication Date
                </label>
                <input
                  type="text"
                  value={formData.publication_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, publication_date: e.target.value }))}
                  placeholder="2024"
                  className="w-full px-3 py-2 border-2 border-[#8B6F47] rounded"
                />
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-[#6B4423] mb-1">
                  Source Type
                </label>
                <select
                  value={formData.source_type}
                  onChange={(e) => setFormData(prev => ({ ...prev, source_type: e.target.value }))}
                  className="w-full px-3 py-2 border-2 border-[#8B6F47] rounded"
                >
                  <option value="website">Website</option>
                  <option value="journal">Journal Article</option>
                  <option value="book">Book</option>
                  <option value="conference">Conference Paper</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-[#6B4423] mb-1">
                  DOI
                </label>
                <input
                  type="text"
                  value={formData.doi}
                  onChange={(e) => setFormData(prev => ({ ...prev, doi: e.target.value }))}
                  placeholder="10.1234/example"
                  className="w-full px-3 py-2 border-2 border-[#8B6F47] rounded"
                />
              </div>
            </div>
            
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setShowAddForm(false)}
                className="retro-button bg-gray-500 hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="retro-button"
              >
                {loading ? 'Adding...' : 'Add Citation'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Citations List */}
      <div className="space-y-3">
        {citations.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No citations yet. Add your first citation above.
          </div>
        ) : (
          citations.map((citation, index) => (
            <div
              key={citation.id}
              className="p-4 bg-white border-2 border-[#8B6F47] rounded hover:border-[#6B4423] transition-colors"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-bold text-[#6B4423]">[{index + 1}]</span>
                    <h4 className="font-semibold text-lg">{citation.title}</h4>
                  </div>
                  
                  {citation.authors && citation.authors.length > 0 && (
                    <p className="text-sm text-gray-700 mb-1">
                      <strong>Authors:</strong> {citation.authors.join(', ')}
                    </p>
                  )}
                  
                  {citation.publication_date && (
                    <p className="text-sm text-gray-700 mb-1">
                      <strong>Date:</strong> {citation.publication_date}
                    </p>
                  )}
                  
                  {citation.source_url && (
                    <a
                      href={citation.source_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                    >
                      <ExternalLink className="w-3 h-3" />
                      {citation.source_url}
                    </a>
                  )}
                  
                  <div className="mt-2">
                    <span className="inline-block px-2 py-1 bg-[#F4E4C1] text-xs rounded">
                      {citation.source_type}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Summary */}
      <div className="mt-6 p-4 bg-[#F4E4C1] rounded border-2 border-[#8B6F47]">
        <div className="flex items-center justify-between text-sm">
          <span className="font-semibold text-[#6B4423]">
            Total Citations: {citations.length}
          </span>
          <span className="text-gray-700">
            Citation Style: {citationStyle}
          </span>
        </div>
      </div>
    </div>
  );
}
